import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { YlyGrid } from './shared/YlyGrid'
import { DualGrid } from './shared/DualGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneYearData } from '../../hooks/useDlyOneYearData'
import { SUN_SCALE, RAD_SCALE } from './shared/weatherUtils'

const SLUG = 'dly-one-year-sun'

function sunCellStyle(value) {
  if (value == null || value < 0.1) return null
  const entry = SUN_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function radCellStyle(value) {
  if (value == null) return null
  const entry = RAD_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function sunFormat(value) {
  if (value == null) return '—'
  return value.toFixed(1)
}

function radFormat(value) {
  if (value == null) return '—'
  return String(Math.round(value))
}

function SunLegend() {
  return (
    <div className="shrink-0 flex flex-col gap-0.5">
      <div className="text-xs font-semibold text-gray-400 tracking-wide mb-1">hod.</div>
      {SUN_SCALE.map(({ label, bg }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="inline-block w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: bg }} />
          <span className="text-gray-600">{label}</span>
        </div>
      ))}
      <div className="flex items-center gap-1.5 text-xs whitespace-nowrap mt-0.5">
        <span className="inline-block w-4 h-4 rounded-sm shrink-0 border border-gray-200" />
        <span className="text-gray-400">{'= 0 hod.'}</span>
      </div>
    </div>
  )
}

function RadLegend() {
  return (
    <div className="shrink-0 flex flex-col gap-0.5">
      <div className="text-xs font-semibold text-gray-400 tracking-wide mb-1">kJ/m²</div>
      {RAD_SCALE.map(({ label, bg }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="inline-block w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: bg }} />
          <span className="text-gray-600">{label}</span>
        </div>
      ))}
    </div>
  )
}

export function DlyOneYearSunView({ station }) {
  const { year, setYear } = useYearPeriodStore()

  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap: sunMap, isLoading: l1, isError: e1 } = useDlyOneYearData(station?.gh_id, year, 'DLY_SUNSHINESUM')
  const { dataMap: radMap, isLoading: l2, isError: e2 } = useDlyOneYearData(station?.gh_id, year, 'DLY_GLOBRADSUM')

  const isLoading = l1 || l2
  const isError   = e1 || e2
  const hasRad    = radMap.size > 0

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !isError && !hasRad && (
          <div className="flex-1 min-h-0 flex gap-6">
            <div className="flex-1 min-w-0 min-h-0">
              <YlyGrid year={year} dataMap={sunMap} getCellStyle={sunCellStyle} formatCell={sunFormat} orientation={orientation} />
            </div>
            <SunLegend />
          </div>
        )}
        {!isLoading && !isError && hasRad && (
          <div className="flex-1 min-h-0 flex gap-6">
            <div className="flex-1 min-w-0 min-h-0">
              <DualGrid
                year={year}
                orientation={orientation}
                left={{  dataMap: sunMap, getCellStyle: sunCellStyle, formatCell: sunFormat, title: 'Sluneční svit (hod.)' }}
                right={{ dataMap: radMap, getCellStyle: radCellStyle, formatCell: radFormat, title: 'Globální záření (kJ/m²)' }}
              />
            </div>
            <div className="shrink-0 flex flex-col gap-6">
              <SunLegend />
              <RadLegend />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
